import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { BsTrash, BsPlus } from "react-icons/bs";

const LineItemsTable = ({ lineItems, setLineItems, readOnly = false }) => {
  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: Date.now(),
        itemName: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        unit: "pcs",
        totalPrice: 0,
      },
    ]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.id === id || item._id === id) {
          const updated = { ...item, [field]: value };
          // Auto-calculate total price
          if (field === "quantity" || field === "unitPrice") {
            updated.totalPrice =
              (updated.quantity || 0) * (updated.unitPrice || 0);
          }
          return updated;
        }
        return item;
      }),
    );
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id && item._id !== id));
  };

  const calculateGrandTotal = () => {
    return lineItems.reduce((sum, item) => {
      const total = (item.quantity || 0) * (item.unitPrice || 0);
      return sum + total;
    }, 0);
  };

  if (readOnly) {
    return (
      <Box>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Item Name</Th>
              <Th>Description</Th>
              <Th isNumeric>Quantity</Th>
              <Th>Unit</Th>
              <Th isNumeric>Unit Price</Th>
              <Th isNumeric>Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {lineItems.map((item, index) => (
              <Tr key={item._id || item.id || index}>
                <Td>{item.itemName}</Td>
                <Td>{item.description}</Td>
                <Td isNumeric>{item.quantity}</Td>
                <Td>{item.unit || "pcs"}</Td>
                <Td isNumeric>₹{item.unitPrice?.toFixed(2)}</Td>
                <Td isNumeric fontWeight="semibold">
                  ₹{((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Box mt={4} textAlign="right">
          <Text fontSize="lg" fontWeight="bold">
            Grand Total: ₹{calculateGrandTotal().toFixed(2)}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Item Name *</Th>
              <Th>Description</Th>
              <Th isNumeric>Qty *</Th>
              <Th>Unit</Th>
              <Th isNumeric>Unit Price *</Th>
              <Th isNumeric>Total</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {lineItems.map((item, index) => (
              <Tr key={item._id || item.id || index}>
                <Td>
                  <Input
                    size="sm"
                    value={item.itemName}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "itemName",
                        e.target.value,
                      )
                    }
                    placeholder="Item name"
                  />
                </Td>
                <Td>
                  <Input
                    size="sm"
                    value={item.description}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "description",
                        e.target.value,
                      )
                    }
                    placeholder="Description"
                  />
                </Td>
                <Td>
                  <NumberInput
                    size="sm"
                    min={1}
                    value={item.quantity}
                    onChange={(valueString) =>
                      updateLineItem(
                        item._id || item.id,
                        "quantity",
                        parseInt(valueString) || 1,
                      )
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Td>
                <Td>
                  <Input
                    size="sm"
                    value={item.unit}
                    onChange={(e) =>
                      updateLineItem(
                        item._id || item.id,
                        "unit",
                        e.target.value,
                      )
                    }
                    placeholder="Unit"
                    maxW="80px"
                  />
                </Td>
                <Td>
                  <NumberInput
                    size="sm"
                    min={0}
                    precision={2}
                    value={item.unitPrice}
                    onChange={(valueString) =>
                      updateLineItem(
                        item._id || item.id,
                        "unitPrice",
                        parseFloat(valueString) || 0,
                      )
                    }
                  >
                    <NumberInputField />
                  </NumberInput>
                </Td>
                <Td isNumeric fontWeight="semibold">
                  ₹{item.totalPrice?.toFixed(2) || "0.00"}
                </Td>
                <Td>
                  <IconButton
                    size="sm"
                    icon={<BsTrash />}
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeLineItem(item._id || item.id)}
                    aria-label="Remove item"
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <HStack justify="space-between">
        <Button
          leftIcon={<BsPlus />}
          onClick={addLineItem}
          size="sm"
          variant="outline"
          colorScheme="blue"
        >
          Add Line Item
        </Button>
        <Text fontSize="lg" fontWeight="bold">
          Grand Total: ₹{calculateGrandTotal().toFixed(2)}
        </Text>
      </HStack>
    </VStack>
  );
};

export default LineItemsTable;
